import React, { useMemo } from "react"

import { Form, Formik } from "formik"
import { NotificationManager } from "react-notifications"
import { useHistory } from "react-router-dom"
import { gql, useMutation } from "urql"

import { path } from "~Tutor/TutorRoutes"
import Panel from "~tailwindui/Basics/Panel"
import {
  CheckboxInput,
  Failures,
  SubmitButton,
  handleFailure,
} from "~tailwindui/Form"

import { RunMutation } from "src/types"

export type EditSubjectBucketsProps = {
  tier: {
    id: string
    subjectBuckets: {
      id: string
      name: string
    }[]
  }
  viewer: {
    id: string
    subjectBuckets: {
      id: string
      name: string
      approvalState: string
    }[]
  }
}

const EditSubjectBuckets: React.FC<EditSubjectBucketsProps> = ({
  tier,
  viewer,
}) => {
  const history = useHistory()

  const formState = {
    tierId: tier.id,
    subjectBucketIds: viewer.subjectBuckets.map(bucket => bucket.id),
  }

  const [, runMutation]: [any, RunMutation] = useMutation(
    updateTutorSubjectsMutation
  )

  const handleSubmit = (values, actions) => {
    runMutation(values)
      .then(result => {
        const { failures } = result.data.updateTutorSubjects
        if (failures.length > 0) handleFailure(actions, failures)
        else {
          history.push(path("subjectBucketsIndex"))
          NotificationManager.success(
            "Your subjects have been updated. If approval is necessary, an admin will review them shortly."
          )
        }
      })
      .catch(() => handleFailure(actions))
      .finally(() => actions.setSubmitting(false))
  }

  const subjectBucketOptions = useMemo(() => {
    return tier.subjectBuckets.map(bucket => {
      const tutorSubjectBucket = viewer.subjectBuckets.find(
        sb => sb.id === bucket.id
      )

      return {
        label: `${bucket.name}${
          tutorSubjectBucket?.approvalState === "submitted" ? " (pending)" : ""
        }`,
        value: bucket.id,
        disabled: tutorSubjectBucket?.approvalState === "rejected",
      }
    })
  }, [tier, viewer])

  return (
    <Panel className="min-w-full sm:min-w-[30vw]">
      <Formik initialValues={formState} onSubmit={handleSubmit}>
        <Form>
          <CheckboxInput
            name="subjectBucketIds"
            checkboxType="multi"
            options={subjectBucketOptions}
          />

          <Failures />
          <SubmitButton className="mt-4 w-full" />
        </Form>
      </Formik>
    </Panel>
  )
}

const updateTutorSubjectsMutation = gql`
  mutation UpdateTutorSubjects($tierId: ID!, $subjectBucketIds: [ID!]!) {
    updateTutorSubjects(tierId: $tierId, subjectBucketIds: $subjectBucketIds) {
      failures {
        message
      }
    }
  }
`

export default EditSubjectBuckets
